import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { credentialTypeApi } from '@/services/credentialTypeApi';
import type { CredentialType } from '@/types/credentialType';

export const useCredentialTypeStore = defineStore('credentialType', () => {
  const credentialTypes = ref<CredentialType[]>([]);
  const loading = ref(false);
  const loaded = ref(false);
  const error = ref<string | null>(null);

  const activeTypes = computed(() =>
    credentialTypes.value
      .filter(t => t.isActive)
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
  );

  const byCode = computed<Record<string, CredentialType>>(() => {
    const map: Record<string, CredentialType> = {};
    for (const t of credentialTypes.value) {
      map[t.code.toLowerCase()] = t;
    }
    return map;
  });

  const activeCodes = computed(() => activeTypes.value.map(t => t.code));

  function getByCode(code: string | null | undefined): CredentialType | undefined {
    if (!code) return undefined;
    return byCode.value[code.toLowerCase()];
  }

  async function fetch(force = false): Promise<void> {
    if (loaded.value && !force) return;
    loading.value = true;
    error.value = null;
    try {
      credentialTypes.value = await credentialTypeApi.getAll();
      loaded.value = true;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch credential types';
    } finally {
      loading.value = false;
    }
  }

  async function ensureLoaded(): Promise<void> {
    if (!loaded.value && !loading.value) {
      await fetch();
    }
  }

  return {
    credentialTypes,
    loading,
    loaded,
    error,
    activeTypes,
    activeCodes,
    byCode,
    getByCode,
    fetch,
    ensureLoaded,
  };
});
